import React, { useState } from 'react';
import { IconButton, Tooltip, CircularProgress } from '@mui/material';
import { PictureAsPdf as PdfIcon } from '@mui/icons-material';
import api from '../../config/axios';
import czechTranslations from '../../translations/czech';
import useModernAlert from '../../hooks/useModernAlert';

interface PDFDownloadProps {
  invoiceId: number;
  invoiceNumber: string;
  hasPdf?: boolean;
  size?: 'small' | 'medium' | 'large';
}

const PDFDownload: React.FC<PDFDownloadProps> = ({ 
  invoiceId, 
  invoiceNumber, 
  hasPdf = true,
  size = 'small' 
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { showError, AlertComponent } = useModernAlert();

  const handleDownload = async () => {
    if (!hasPdf) return;
    
    setIsDownloading(true);
    try {
      const response = await api.get(`/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `invoice_${invoiceNumber}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error: any) {
      console.error('PDF download error:', error);
      if (error.response?.status === 404) {
        showError(czechTranslations.alerts.pdfNotFound);
      } else {
        showError(czechTranslations.alerts.pdfDownloadError);
      }
    } finally {
      setIsDownloading(false);
    }
  };

  if (!hasPdf) {
    return (
      <Tooltip title="PDF není k dispozici">
        <span>
          <IconButton size={size} disabled>
            <PdfIcon />
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip title="Stáhnout PDF">
        <IconButton 
          size={size} 
          onClick={handleDownload}
          disabled={isDownloading}
          color="primary"
        >
          {isDownloading ? (
            <CircularProgress size={size === 'small' ? 16 : 24} />
          ) : (
            <PdfIcon />
          )}
        </IconButton>
      </Tooltip>
      {AlertComponent}
    </>
  );
};

export default PDFDownload;
